import { IConfig } from '@microsoft/applicationinsights-common'
import { IConfiguration } from '@microsoft/applicationinsights-core-js'
import { ApplicationInsights } from '@microsoft/applicationinsights-web'

import { ProjectEditor } from '@/apps/brelag/mandator-user/project-editor/projectEditor'
import router from '@/router/index'
import { frontendVersion } from '@/store/GlobalStore'
import store from '@/store/index'

type AppInsightsConfig = IConfiguration & IConfig

export interface TrackEventMeta {
  [key: string]: any
}

export interface TrackEventProperties {
  user_id: string
  project_id: string
  mandator_id: string
  devices: string[] | null
  active_tab: string
  building_id?: string | null
  floor_id?: string | null
  group_id?: string | null
  meta?: TrackEventMeta
}

export interface AppInsightsEvent {
  name: ProjectEditorEventName
  properties?: TrackEventProperties
}

export type ProjectEditorEventName =
  | 'project-editor-lock-project'
  | 'project-editor-unlock-project'
  | 'project-editor-create-project'
  | 'project-editor-create-project-save'
  | 'project-editor-create-project-cancel'
  | 'project-editor-edit-project'
  | 'project-editor-edit-project-save'
  | 'project-editor-edit-project-cancel'
  | 'project-editor-create-building'
  | 'project-editor-create-building-save'
  | 'project-editor-create-building-cancel'
  | 'project-editor-edit-building'
  | 'project-editor-edit-building-save'
  | 'project-editor-edit-building-cancel'
  | 'project-editor-create-floor'
  | 'project-editor-create-floor-save'
  | 'project-editor-create-floor-cancel'
  | 'project-editor-edit-floor'
  | 'project-editor-edit-floor-save'
  | 'project-editor-edit-floor-cancel'
  | 'project-editor-create-device'
  | 'project-editor-create-device-save'
  | 'project-editor-create-device-cancel'
  | 'project-editor-edit-devices'
  | 'project-editor-edit-device-save'
  | 'project-editor-edit-device-cancel'
  | 'project-editor-create-link'
  | 'project-editor-create-link-save'
  | 'project-editor-create-link-cancel'
  | 'project-editor-create-group'
  | 'project-editor-create-group-save'
  | 'project-editor-create-group-cancel'
  | 'project-editor-add-to-group'
  | 'project-editor-add-to-group-save'
  | 'project-editor-add-to-group-cancel'
  | 'project-editor-add-annotation'
  | 'project-editor-print-parts-list'
  | 'project-editor-print-labels'
  | 'project-editor-print-building-plan'
  | 'project-editor-delete-project'

export class AppInsightHandler {
  static install(app, config: AppInsightsConfig): void {
    app.provide('appInsightHandler', new AppInsightHandler(config))
  }

  private appInsights: ApplicationInsights
  private baseName = 'STYLER'
  private trackingEnabled = true

  public constructor(config: AppInsightsConfig) {
    config.enableAutoRouteTracking = true
    this.appInsights = new ApplicationInsights({ config })
    this.appInsights.loadAppInsights()
    this.setupPageTracking()
  }

  /**
   * Tracks a custom event
   */
  public trackProjectEditorEvent(
    name: ProjectEditorEventName,
    properties?: TrackEventProperties,
    condition: (
      lastEvent: AppInsightsEvent,
      currentEvent: AppInsightsEvent
    ) => boolean = (lastEvent, currentEvent) => true
  ): void {
    if (this.trackingEnabled) {
      try {
        const projectEditor = ProjectEditor.getInstance(store)
        if (!projectEditor) {
          console.warn('ProjectEditor not found, cannot track event')
          return
        }
        const props: TrackEventProperties = {
          user_id: store.state.global.profileId,
          project_id: projectEditor.project?.id,
          mandator_id: projectEditor.mandator?.id,
          devices: projectEditor.selectedDeviceIds,
          active_tab: store.getters['brelag/activeTab'],
          building_id: projectEditor.selectedBuilding,
          floor_id: projectEditor.selectedFloor?.id,
          group_id: projectEditor.selectedGroup?.id,
          ...properties,
        }
        if (
          !condition(store.getters['global/lastAppInsightsEvent'], {
            name: name,
            properties: props,
          })
        ) {
          // Condition for event to trigger was not met. Do not track.
          return
        }
        store.commit('global/setLastAppInsightsEvent', {
          name: name,
          properties: props,
        })
        this.appInsights.trackEvent({ name: name, properties: props })
      } catch (error) {
        console.error(error)
      }
    }
  }

  public getLastEvent(): AppInsightsEvent | null {
    return store.getters['global/lastAppInsightsEvent']
  }

  /**
   * Sets up tracking between pages
   */
  private setupPageTracking() {
    router.beforeEach((to, from, next) => {
      const name = this.baseName + ' / ' + to.name
      this.appInsights.startTrackPage(name)
      next()
    })

    router.afterEach((route) => {
      const name = this.baseName + ' / ' + route.name
      const url = location.protocol + '//' + location.host + route.fullPath
      const properties = {
        user_id: store.state.global.profileId,
        organisation_id: store.state.global.context.selection.organisation.id,
        app_version: frontendVersion,
        platform: 'web',
      }
      this.appInsights.stopTrackPage(name, url, properties)
      this.appInsights.flush(true)
    })
  }
}
